import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface DashboardData {
  products: any[];
  orders: any[];
  escrowAccount: any;
}

interface UseDashboardDataResult {
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

export const useDashboardData = (userId: string | undefined): UseDashboardDataResult => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cacheRef = useRef<{ data: DashboardData; timestamp: number } | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async (force = false) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    // Check cache first
    if (!force && cacheRef.current) {
      const { data: cachedData, timestamp } = cacheRef.current;
      const isExpired = Date.now() - timestamp > CACHE_DURATION;
      
      if (!isExpired) {
        setData(cachedData);
        setLoading(false);
        return;
      }
    }

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      setLoading(true);
      setError(null);

      // Run all queries in parallel with optimized selections
      const [productsResult, ordersResult, escrowResult] = await Promise.allSettled([
        supabase
          .from('products')
          .select('id, title, status, views, likes')
          .eq('seller_id', userId),
        supabase
          .from('orders')
          .select(`
            id, 
            buyer_id, 
            total_amount, 
            status, 
            items,
            shipping_address,
            tracking_number,
            created_at
          `)
          .eq('seller_id', userId)
          .order('created_at', { ascending: false })
          .limit(50),
        supabase
          .from('escrow_accounts')
          .select('balance_available, balance_pending, balance_released')
          .eq('seller_id', userId)
          .maybeSingle()
      ]);

      // Handle results
      const products = productsResult.status === 'fulfilled' ? productsResult.value.data || [] : [];
      const orders = ordersResult.status === 'fulfilled' ? ordersResult.value.data || [] : [];
      const escrowAccount = escrowResult.status === 'fulfilled' ? escrowResult.value.data || {} : {};

      // Log any errors
      if (productsResult.status === 'rejected') {
        console.error('Products fetch error:', productsResult.reason);
      }
      if (ordersResult.status === 'rejected') {
        console.error('Orders fetch error:', ordersResult.reason);
      }
      if (escrowResult.status === 'rejected') {
        console.error('Escrow fetch error:', escrowResult.reason);
      }

      const dashboardData: DashboardData = {
        products,
        orders,
        escrowAccount
      };

      // Update cache
      cacheRef.current = {
        data: dashboardData,
        timestamp: Date.now()
      };

      setData(dashboardData);
    } catch (err: any) {
      if (!signal.aborted) {
        setError(err.message || 'Failed to fetch dashboard data');
        console.error('Dashboard data fetch error:', err);
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [userId]);

  const refetch = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    fetchData();

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch
  };
};